import * as React from "react";
import { Link } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import { PRIMARY_CTA_TARGET } from "./constants";
import { trackEvent, type SolutionEvent } from "./analytics";

type SolutionCtaProps = {
  /** Defaults to the shared primary target so callers rarely set it. */
  to?: string;
  /** Query params for internal links, e.g. the `source` tag /contact reads. */
  search?: Record<string, string>;
  children: React.ReactNode;
  event: SolutionEvent;
  eventDetail?: string;
  className?: string;
  variant?: React.ComponentProps<typeof Button>["variant"];
  size?: React.ComponentProps<typeof Button>["size"];
};

/** An in-app route vs. something we must hand to the browser. */
function isExternal(target: string): boolean {
  return (
    /^(https?:)?\/\//.test(target) ||
    target.startsWith("mailto:") ||
    target.startsWith("tel:")
  );
}

/**
 * The single CTA used across the candidate pages.
 *
 * It exists so that swapping `/contact` for a real booking tool later is a
 * one-line change to `PRIMARY_CTA_TARGET`: this component already renders an
 * external URL as a plain anchor and an internal path as a router `Link`, so no
 * call site has to change when the target's kind changes.
 */
export function SolutionCta({
  to = PRIMARY_CTA_TARGET,
  search,
  children,
  event,
  eventDetail,
  className,
  variant,
  size,
}: SolutionCtaProps) {
  const handleClick = () => trackEvent(event, eventDetail);

  if (isExternal(to)) {
    // Only http(s) destinations open in a new tab. `mailto:` (and `tel:`) hand
    // off to another application, so `target="_blank"` leaves the user staring
    // at a stranded blank tab in several browsers.
    const opensInNewTab = /^https?:/i.test(to);

    return (
      <Button asChild className={className} variant={variant} size={size}>
        <a
          href={to}
          onClick={handleClick}
          {...(opensInNewTab
            ? { target: "_blank", rel: "noopener noreferrer" }
            : {})}
        >
          {children}
        </a>
      </Button>
    );
  }

  return (
    <Button asChild className={className} variant={variant} size={size}>
      <Link to={to} search={search} onClick={handleClick}>
        {children}
      </Link>
    </Button>
  );
}
